'use client';

import { type ChangeEvent, useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'next-i18next';
import { Button, Input } from '@components/common/Forms';
import { TailwindSpinner } from '@components/common';
import { showBuyNeuroModal } from '@components/features/modals/buy-neuro/BuyNeuroModal';
import { BundleBenefits } from '@components/templates/employer/job-bundles/cards/BundleBenefits';
import { useCreditsCalculator } from '@hooks/credits/useCreditsCalculator';
import { useExtendedRouter } from '@hooks/next-routing-wrappers';

interface MissionControlHireServicesCardsProps {
  isAuth: boolean;
  bundlesData?: ApiSchema<'OfferBundle'>[];
}

type ServiceTrack = 'applicants' | 'interviews';
type ServiceMode = 'self-service' | 'ai-enhanced';
type ApplicantsSelfServiceBundleView = 'start' | 'plus';
type QuantityByBundleKey = Record<string, number>;

export const MissionControlHireServicesCards = ({ isAuth, bundlesData }: MissionControlHireServicesCardsProps) => {
  const { t } = useTranslation('common');
  const { router } = useExtendedRouter();

  const [serviceTrack, setServiceTrack] = useState<ServiceTrack>('applicants');
  const [serviceMode, setServiceMode] = useState<ServiceMode>('ai-enhanced');
  const [applicantsSelfServiceBundleView, setApplicantsSelfServiceBundleView] = useState<ApplicantsSelfServiceBundleView>('start');
  const [quantityByBundleKey, setQuantityByBundleKey] = useState<QuantityByBundleKey>({});
  const [creditAmount, setCreditAmount] = useState(0);
  const [hasTopUpBeenAdjusted, setHasTopUpBeenAdjusted] = useState(false);

  const plusBundle = bundlesData?.find((bundle) => !bundle?.parentBundleId && bundle?.identifier === 'plus-fix');
  const proBundle = bundlesData?.find((bundle) => !bundle?.parentBundleId && bundle?.identifier === 'pro-fix');
  const hireBundle = bundlesData?.find((bundle) => !bundle?.parentBundleId && bundle?.identifier === 'shire-fix');
  const standardBundle = bundlesData?.find((bundle) => !bundle?.parentBundleId && bundle?.identifier === 'standard-fix');
  const liveBundle = bundlesData?.find(
    (bundle) =>
      !bundle?.parentBundleId &&
      String(bundle?.identifier ?? '')
        .toLowerCase()
        .includes('live'),
  );

  const isApplicantsSelfService = serviceTrack === 'applicants' && serviceMode === 'self-service';
  const isInterviewSelfService = serviceTrack === 'interviews' && serviceMode === 'self-service';
  const isLiveSlotEnabled = isInterviewSelfService && Boolean(liveBundle);

  const selectedBundle = useMemo(() => {
    if (serviceTrack === 'interviews' && serviceMode === 'self-service') return liveBundle ?? standardBundle;
    if (serviceTrack === 'applicants' && serviceMode === 'ai-enhanced') return proBundle;
    if (serviceTrack === 'interviews' && serviceMode === 'ai-enhanced') return hireBundle;
    if (serviceTrack === 'applicants' && serviceMode === 'self-service') {
      return applicantsSelfServiceBundleView === 'plus' ? plusBundle : standardBundle;
    }
    return standardBundle;
  }, [serviceTrack, serviceMode, applicantsSelfServiceBundleView, proBundle, hireBundle, plusBundle, standardBundle, liveBundle]);

  const selectedTitle = useMemo(() => {
    if (serviceTrack === 'interviews' && serviceMode === 'self-service') return liveBundle?.title || 'Live';
    if (serviceTrack === 'applicants' && serviceMode === 'ai-enhanced') return 'Pro';
    if (serviceTrack === 'interviews' && serviceMode === 'ai-enhanced') return 'Hire';
    if (serviceTrack === 'applicants' && serviceMode === 'self-service') {
      return applicantsSelfServiceBundleView === 'plus' ? 'Start Plus' : 'Start';
    }
    return 'Start';
  }, [serviceTrack, serviceMode, applicantsSelfServiceBundleView, liveBundle]);

  const selectedSubtitle = useMemo(() => {
    if (serviceTrack === 'interviews' && serviceMode === 'self-service') {
      if (!liveBundle) return t('app.global.comingsoon');
      return liveBundle.subtitle || t('job.bundle.live.benefit.duration') || '';
    }
    if (serviceTrack === 'applicants' && serviceMode === 'ai-enhanced') return t('mc.services.bundle.pro.subtitle');
    if (serviceTrack === 'interviews' && serviceMode === 'ai-enhanced') return t('mc.services.bundle.hire.subtitle');
    if (serviceTrack === 'applicants' && serviceMode === 'self-service') {
      if (applicantsSelfServiceBundleView === 'plus') return t('mc.services.bundle.plus.subtitle');
      return t('mc.services.bundle.start_view.subtitle');
    }
    return t('mc.services.bundle.start.subtitle');
  }, [serviceTrack, serviceMode, applicantsSelfServiceBundleView, liveBundle, t]);

  const showBenefitsOnHover = !isApplicantsSelfService && (!isInterviewSelfService || isLiveSlotEnabled);
  const resolvedBundleId = selectedBundle?.id ?? '';
  const isActivateDisabled = (isInterviewSelfService && !isLiveSlotEnabled) || !resolvedBundleId;
  const selectedBundleKey = useMemo(() => {
    if (isInterviewSelfService) return 'interviews-self-service-live';
    if (isApplicantsSelfService) return `applicants-self-service-${applicantsSelfServiceBundleView}`;
    if (serviceTrack === 'applicants' && serviceMode === 'ai-enhanced') return 'applicants-ai-enhanced-pro';
    if (serviceTrack === 'interviews' && serviceMode === 'ai-enhanced') return 'interviews-ai-enhanced-hire';
    return `${serviceTrack}-${serviceMode}`;
  }, [serviceTrack, serviceMode, applicantsSelfServiceBundleView, isApplicantsSelfService, isInterviewSelfService]);

  const selectedUnitPrice = Math.ceil(selectedBundle?.price ?? 0);
  const quantity = quantityByBundleKey[selectedBundleKey] ?? 1;
  const selectedFullPrice = Math.ceil(selectedBundle?.fullPrice ?? 0);
  const isDiscounted = selectedFullPrice > 0 && selectedFullPrice > selectedUnitPrice;
  const initialTopUpAmount = useMemo(
    () => Math.ceil(plusBundle?.price ?? 0) + Math.ceil(proBundle?.price ?? 0) + Math.ceil(hireBundle?.price ?? 0) + Math.ceil(standardBundle?.price ?? 0),
    [plusBundle?.price, proBundle?.price, hireBundle?.price, standardBundle?.price],
  );

  useEffect(() => {
    if (hasTopUpBeenAdjusted) return;
    if (initialTopUpAmount <= 0) return;
    setCreditAmount(initialTopUpAmount);
  }, [hasTopUpBeenAdjusted, initialTopUpAmount]);

  const { priceData, priceIsValidating } = useCreditsCalculator(creditAmount);
  const discountLevels = priceData?.discountLevels ?? [];
  const nextTier = discountLevels.filter((level) => (level.minAmount ?? 0) > creditAmount).sort((a, b) => (a.minAmount ?? 0) - (b.minAmount ?? 0))[0];

  const differenceToNextTier = nextTier ? Math.round((nextTier.minAmount ?? 0) - creditAmount) : 0;
  const nextTierPercentage = nextTier?.percentage;
  const showNextTierSuggestion = nextTier && differenceToNextTier > 0;

  const setBundleType = (nextTrack: ServiceTrack, nextMode: ServiceMode) => {
    setServiceTrack(nextTrack);
    setServiceMode(nextMode);
    if (nextTrack === 'applicants' && nextMode === 'self-service') {
      setApplicantsSelfServiceBundleView('plus');
    }
  };

  const handleTopUp = () => {
    if (!isAuth) {
      void router.push(`/login?targetPath=${encodeURIComponent('/mc')}`, { withLocale: false });
      return;
    }
    showBuyNeuroModal(creditAmount);
  };

  const handleActivateBundle = () => {
    if (isActivateDisabled) return;
    if (!resolvedBundleId) {
      // eslint-disable-next-line no-console
      console.error('[MissionControlHireServicesCards] missing bundle id for selection', { selectedBundle });
      return;
    }

    const targetPath = `/mc/job-add/edit?preselected=${encodeURIComponent(resolvedBundleId)}`;
    if (!isAuth) {
      void router.push(`/login?targetPath=${encodeURIComponent(targetPath)}`, { withLocale: false });
      return;
    }
    void router.push(targetPath);
  };

  return (
    <div className="mt-4 w-full rounded-2xl bg-surface-100">
      <div className="grid grid-cols-1 gap-y-4 md:grid-cols-2 md:gap-x-8">
        <div id="bundle-selector" className="flex h-full min-h-0 flex-col rounded-2xl border bg-surface p-4 shadow-sm">
          <div className="text-left text-xs font-bold uppercase tracking-widest text-ink-medium">{t('mc.home_recruiter.configure_recruitments')}</div>

          <div className="mt-3 grid grid-cols-2 gap-2 rounded-xl bg-surface-100 p-1">
            <button
              type="button"
              onClick={() => setBundleType('applicants', serviceMode)}
              className={`rounded-lg px-3 py-1.5 text-sm font-semibold ${serviceTrack === 'applicants' ? 'bg-surface text-ink shadow-sm' : 'text-ink-medium'}`}
            >
              {t('mc.services.volume_label')}
            </button>
            <button
              type="button"
              onClick={() => setBundleType('interviews', serviceMode)}
              className={`rounded-lg px-3 py-1.5 text-sm font-semibold ${serviceTrack === 'interviews' ? 'bg-surface text-ink shadow-sm' : 'text-ink-medium'}`}
            >
              {t('mc.services.selection_label')}
            </button>
          </div>

          <div className="mt-2 grid grid-cols-2 gap-2 rounded-xl bg-surface-100 p-1">
            <button
              type="button"
              onClick={() => setBundleType(serviceTrack, 'self-service')}
              className={`rounded-lg px-3 py-1.5 text-sm font-semibold ${serviceMode === 'self-service' ? 'bg-surface text-ink shadow-sm' : 'text-ink-medium'}`}
            >
              {t('mc.services.vertical.manual')}
            </button>
            <button
              type="button"
              onClick={() => setBundleType(serviceTrack, 'ai-enhanced')}
              className={`rounded-lg px-3 py-1.5 text-sm font-semibold ${serviceMode === 'ai-enhanced' ? 'bg-surface text-ink shadow-sm' : 'text-ink-medium'}`}
            >
              {t('mc.services.vertical.automatic')}
            </button>
          </div>

          <div className="mt-4 text-left text-xs font-bold uppercase tracking-widest text-ink-medium">{t('mc.hone_recruiter.recruitment_number')}</div>
          <div className="mt-2 flex items-center gap-3">
            <button
              type="button"
              className="flex size-8 items-center justify-center rounded-lg border border-input bg-surface-100 text-lg text-ink-medium"
              onClick={() => {
                if (quantity <= 0) return;
                setQuantityByBundleKey((prev) => ({
                  ...prev,
                  [selectedBundleKey]: Math.max(0, (prev[selectedBundleKey] ?? 1) - 1),
                }));
                setHasTopUpBeenAdjusted(true);
                setCreditAmount((prev) => Math.max(0, prev - selectedUnitPrice));
              }}
            >
              -
            </button>
            <span className="min-w-4 text-center text-base font-semibold text-ink">{quantity}</span>
            <button
              type="button"
              className="flex size-8 items-center justify-center rounded-lg border border-input bg-surface-100 text-lg text-ink-medium"
              onClick={() => {
                setQuantityByBundleKey((prev) => ({
                  ...prev,
                  [selectedBundleKey]: (prev[selectedBundleKey] ?? 1) + 1,
                }));
                setHasTopUpBeenAdjusted(true);
                setCreditAmount((prev) => prev + selectedUnitPrice);
              }}
            >
              +
            </button>
          </div>

          <div id="selected-job-bundle" className="mt-4 flex min-h-0 flex-1 overflow-hidden rounded-xl border border-secondary/20 bg-secondary/5 p-3">
            <div className="grid size-full grid-cols-[auto_minmax(0,1fr)] items-center gap-6">
              <div className="flex flex-col justify-center text-left">
                {isDiscounted && <div className="mb-1 text-sm text-ink-medium line-through">{selectedFullPrice}</div>}
                <div className="flex items-end gap-1">
                  <span className="text-[36px] font-bold leading-none text-secondary">{selectedUnitPrice}</span>
                </div>
                <div className="mt-1 text-sm text-ink-medium">{t('employer.branding.price.desc')}</div>
              </div>
              <div
                id="selected-job-bundle-titles"
                className="group/selected-benefits relative flex size-full min-h-0 flex-col justify-center overflow-hidden text-center"
              >
                <div className={showBenefitsOnHover ? 'group-hover/selected-benefits:hidden' : ''}>
                  <div className="text-xl font-bold text-ink">{selectedTitle}</div>
                  <div className="text-sm text-ink-medium">{selectedSubtitle}</div>
                  {isApplicantsSelfService && (
                    <button
                      type="button"
                      onClick={() => setApplicantsSelfServiceBundleView((prev) => (prev === 'start' ? 'plus' : 'start'))}
                      className="mt-1 text-sm font-semibold text-secondary underline hover:text-secondary"
                    >
                      {applicantsSelfServiceBundleView === 'start' ? t('mc.services.switch_start_plus') : t('mc.services.switch_plus_start')}
                    </button>
                  )}
                </div>
                {showBenefitsOnHover && !!selectedBundle?.benefits?.length && (
                  <div className="absolute inset-0 hidden overflow-hidden text-left group-hover/selected-benefits:block">
                    <div className="h-full overflow-y-auto pr-1">
                      <BundleBenefits benefits={selectedBundle.benefits} />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="mt-3 rounded-md bg-surface-100 p-3 text-left text-xs text-ink">
            <Trans i18nKey="mc.services.extra_services" />
          </div>

          <div className="mt-4">
            <Button onClick={handleActivateBundle} rounding="full" color="secondary" size="sm" disabled={isActivateDisabled}>
              {t('offer.subscription_section.label.activate')}
            </Button>
          </div>
        </div>

        <div id="wallet-bar" className="flex h-full flex-col rounded-2xl border bg-surface p-4 shadow-sm">
          <div className="text-left text-xs font-bold uppercase tracking-widest text-ink-medium">{t('wallet.top-up.order')}</div>
          <div className="mt-3">
            <Input
              id="mcTopUpAmountInline"
              type="number"
              value={creditAmount || ''}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                setHasTopUpBeenAdjusted(true);
                const val = e.target.value;
                if (val === '' || val === null) {
                  setCreditAmount(0);
                  return;
                }
                const numVal = parseInt(val, 10);
                if (!Number.isNaN(numVal)) setCreditAmount(numVal);
              }}
            />
            <span className="mt-1 block text-xs text-ink-medium">{t('neuro.label.order-amount-no-discount')}</span>

            {showNextTierSuggestion && (
              <button
                type="button"
                onClick={() => {
                  setHasTopUpBeenAdjusted(true);
                  setCreditAmount(creditAmount + differenceToNextTier);
                }}
                className="mt-2 inline-flex items-center gap-2 rounded-md bg-secondary/10 px-2 py-1 text-xs font-medium text-secondary hover:bg-secondary/20"
              >
                <Trans
                  i18nKey="page.offer-configurator.bonus_supplement.desc"
                  values={{
                    nrOfCredits: differenceToNextTier,
                    nrPercentageBonus: nextTierPercentage,
                  }}
                />
              </button>
            )}
          </div>

          <div className="mt-4 rounded-md bg-surface-100 p-3 text-sm">
            <div className="flex items-baseline justify-between py-1">
              <span>{t('page.offer.calculator.total.credits')}</span>
              <span>{priceIsValidating ? <TailwindSpinner className="size-4 text-ink-medium" /> : (priceData?.amount ?? 0)}</span>
            </div>
            <div className="flex items-baseline justify-between py-1">
              <span>{t('price.calculator.top-up.bonus_percentage')}</span>
              <span>{priceIsValidating ? <TailwindSpinner className="size-4 text-ink-medium" /> : `${priceData?.bonusPercentage ?? 0}%`}</span>
            </div>
            <div className="flex items-baseline justify-between py-1">
              <span>{t('price.calculator.top-up.bonus_amount')}</span>
              <span>{priceIsValidating ? <TailwindSpinner className="size-4 text-ink-medium" /> : (priceData?.bonusAmount ?? 0)}</span>
            </div>
            <div className="flex items-baseline justify-between py-1">
              <span>{t('price.calculator.top-up.next_bonus_level')}</span>
              <span className="text-right">
                {priceIsValidating ? (
                  <TailwindSpinner className="size-4 text-ink-medium" />
                ) : (
                  <>
                    {priceData?.nextBonusPercentage ?? 0}
                    {'% '}({priceData?.nextBonusAmount ?? 0}
                    {' €'})
                  </>
                )}
              </span>
            </div>
          </div>

          <div className="mt-1 grid grid-cols-2 items-baseline gap-2">
            <span className="text-left text-sm text-ink-medium">{t('page.offer.calculator.total_net.euro')}</span>
            <div className="text-right text-2xl font-bold text-ink">
              {priceIsValidating ? (
                <TailwindSpinner className="ml-auto size-6 text-ink-medium" />
              ) : (
                <>
                  <span>{priceData?.priceToPay ?? creditAmount}</span> <span className="text-sm font-medium">{t('employer.branding.price.desc')}</span>
                </>
              )}
            </div>
          </div>

          <div className="mt-auto pt-4">
            <Button onClick={handleTopUp} rounding="full" color="secondary" size="sm">
              {t('global.buy-neuro')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
